import { useState, useEffect, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  Upload,
  LayoutGrid,
  User as UserIcon,
  CreditCard,
  Shield,
  Home,
  LogOut,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Badge, Button } from './ui';
import ConfirmDialog from './ConfirmDialog';
import { planToBadgeVariant } from '../utils/statusBadge';
import { planLabel } from '../utils/formatters';
import { BILLING_ENABLED } from '../config/featureFlags';
import type { User } from '../types';

interface HeaderProps {
  user?: User | null;
  title?: string;
  backTo?: string;
  backLabel?: string;
  showMenu?: boolean;
}

/** メニュー項目の可視性判定に渡すコンテキスト */
interface MenuContext {
  user: User;
  billingEnabled: boolean;
}

/** メニュー項目の単一ソース定義（PC ドロップダウン・SP ドロワー双方が同一定義から描画） */
interface MenuItemDef {
  key: string;
  label: string;
  /** 遷移先パス（Link）。logout（action項目）では未使用 */
  to?: string;
  icon: LucideIcon;
  /** 表示バリアント。upload=強調CTA / link=通常リンク / logout=赤系アクション */
  variant: 'upload' | 'link' | 'logout';
  /** 表示条件の述語。省略時は常に表示 */
  visibleWhen?: (ctx: MenuContext) => boolean;
}

// ナビゲーション項目の単一ソース（上→下の表示順）。label・パス・アイコンはここだけで定義する。
// 「新規アップロード」はメニュー最上部の強調 CTA。FAQ/利用規約/プライバシーはフッターへ集約済みのため除外。
const MENU_ITEMS: MenuItemDef[] = [
  { key: 'upload', label: '新規アップロード', to: '/upload', icon: Upload, variant: 'upload' },
  { key: 'dashboard', label: 'ダッシュボード', to: '/dashboard', icon: LayoutGrid, variant: 'link' },
  { key: 'profile', label: 'プロフィール', to: '/profile', icon: UserIcon, variant: 'link' },
  {
    key: 'billing',
    label: 'プラン・課金',
    to: '/billing',
    icon: CreditCard,
    variant: 'link',
    visibleWhen: ({ billingEnabled }) => billingEnabled,
  },
  {
    key: 'admin',
    label: '管理者ページ',
    to: '/admin',
    icon: Shield,
    variant: 'link',
    visibleWhen: ({ user }) => user.is_admin,
  },
  { key: 'home', label: 'ホームに戻る', to: '/', icon: Home, variant: 'link' },
  { key: 'logout', label: 'ログアウト', icon: LogOut, variant: 'logout' },
];

export default function Header({
  user,
  title = 'otomochi',
  backTo,
  backLabel,
  showMenu = true,
}: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  // PC ドロップダウンの基準コンテナ。SP のオーバーレイ/ドロワーもこの配下に置くことで、
  // 外側クリック（mousedown）が SP ドロワー内リンクの click を先取りして閉じるのを防ぐ。
  const menuRef = useRef<HTMLDivElement>(null);
  // 閉止時にフォーカスを戻すトリガー（ハンバーガー）。
  const triggerRef = useRef<HTMLButtonElement>(null);
  // 開時に初期フォーカスを移す SP ドロワー本体。
  const drawerRef = useRef<HTMLDivElement>(null);

  const openMenu = () => setMenuOpen(true);

  // 全閉止経路はこの closeMenu に一本化する（フォーカス復帰を一貫化）。
  // setMenuOpen(setter) と triggerRef(ref) のみ参照するため deps 空で安定。
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    triggerRef.current?.focus();
  }, []);

  // ログアウトは確認を挟む（誤クリックで作業中の画面から飛ばされるのを防ぐ）。
  // メニューは先に閉じる（ドロワーの上に確認ダイアログが重なるのを避ける）。
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const requestLogout = () => {
    setMenuOpen(false);
    setShowLogoutConfirm(true);
  };

  // 確認後の実行。closeMenu を経由しない：直後に /login へ遷移しヘッダー（triggerRef）が
  // アンマウントされるためフォーカス復帰は無意味。スクロールロックは menuOpen 追従 effect が解除する。
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // 外側クリックで閉じる（PC ドロップダウン挙動）。menuOpen 中のみ有効。
  // SP のオーバーレイ/ドロワーは menuRef 配下のため、この経路では閉じない（オーバーレイ onClick / ESC が担う）。
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, closeMenu]);

  // ESC キーで閉じる／Tab はドロワー内にフォーカスを封じ込める（フォーカストラップ）。
  // PC では SP ドロワーが display:none でフォーカスが入らないため、トラップ分岐は自然に no-op。
  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
        return;
      }
      if (event.key !== 'Tab') return;
      const drawer = drawerRef.current;
      if (!drawer) return;
      const focusable = drawer.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        // 先頭（またはドロワー本体）で Shift+Tab → 末尾へループ。
        if (active === first || active === drawer) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        // 末尾で Tab → 先頭へループ。
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, closeMenu]);

  // 背景スクロールロック（SP 限定）。JS ではビューポート判定をせず body に
  // drawer-open クラスを付与し、実際のロックは index.css の md 未満メディアクエリで行う。
  useEffect(() => {
    if (!menuOpen) return;
    document.body.classList.add('drawer-open');
    return () => document.body.classList.remove('drawer-open');
  }, [menuOpen]);

  // 開時に SP ドロワーへ初期フォーカスを移す（PC ではドロワーが display:none のため no-op）。
  useEffect(() => {
    if (!menuOpen) return;
    drawerRef.current?.focus();
  }, [menuOpen]);

  // メニュー項目1件を variant に応じて描画（単一ソース）。
  const renderItem = (item: MenuItemDef): ReactNode => {
    const Icon = item.icon;
    if (item.variant === 'upload') {
      return (
        <Link
          key={item.key}
          to={item.to!}
          onClick={closeMenu}
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-control bg-primary text-white font-medium hover:bg-primary-hover transition-colors"
        >
          <Icon className="w-5 h-5" />
          {item.label}
        </Link>
      );
    }
    if (item.variant === 'logout') {
      return (
        <button
          key={item.key}
          onClick={requestLogout}
          className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors w-full"
        >
          <Icon className="w-5 h-5" />
          {item.label}
        </button>
      );
    }
    return (
      <Link
        key={item.key}
        to={item.to!}
        onClick={closeMenu}
        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <Icon className="w-5 h-5" />
        {item.label}
      </Link>
    );
  };

  // メニュー本体（PC/SP で共有描画）。すべての項目は MENU_ITEMS を単一ソースとして描画する。
  const renderMenuBody = (u: User): ReactNode => {
    const ctx: MenuContext = { user: u, billingEnabled: BILLING_ENABLED };
    const visibleItems = MENU_ITEMS.filter((item) => !item.visibleWhen || item.visibleWhen(ctx));
    const uploadItems = visibleItems.filter((item) => item.variant === 'upload');
    const linkItems = visibleItems.filter((item) => item.variant === 'link');
    const logoutItems = visibleItems.filter((item) => item.variant === 'logout');

    const hasDisplayName = Boolean(u.display_name && u.display_name.trim());
    const badgeLabel = planLabel(u.plan.plan_type);

    return (
      <>
        {/* 新規アップロード（最上部の強調 CTA） */}
        <div className="px-3 pt-3 pb-2">{uploadItems.map(renderItem)}</div>

        {/* ユーザー情報（display_name 未設定時はメール1行） */}
        <div className="px-4 py-3 border-b border-t">
          {hasDisplayName ? (
            <>
              <div className="font-medium text-text truncate">{u.display_name}</div>
              <div className="text-sm text-gray-500 truncate">{u.email}</div>
            </>
          ) : (
            <div className="font-medium text-text truncate">{u.email}</div>
          )}
          {badgeLabel && (
            <div className="mt-1">
              <Badge variant={planToBadgeVariant(u.plan.plan_type)} size="sm">
                {badgeLabel}プラン
              </Badge>
            </div>
          )}
        </div>

        {/* ナビゲーション項目 */}
        <div className="py-1">{linkItems.map(renderItem)}</div>

        {/* ログアウト */}
        <div className="border-t py-1">{logoutItems.map(renderItem)}</div>
      </>
    );
  };

  return (
    <>
      {/* 固定ヘッダー。sticky は一部レイアウト/ブラウザで追従しないことがあるため fixed で確実に追従させ、
          直後の同高スペーサー（h-14/md:h-16）でコンテンツ分の高さを確保する（重なり防止）。 */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 h-14 md:h-16 flex justify-between items-center">
        {/* 左側: タイトルまたは戻るリンク */}
        <div className="flex items-center gap-4">
          {backTo ? (
            <Link to={backTo} className="text-primary hover:underline">
              ← {backLabel || 'ダッシュボードに戻る'}
            </Link>
          ) : (
            // ログイン中はロゴからダッシュボードへ、未ログイン（user が null/undefined）はトップへ。
            // user は optional prop のため、未渡し（undefined）も truthy 判定で未ログイン扱いになる。
            <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2">
              <img src="/images/logo.png" alt="otomochi" className="h-7 md:h-8 w-auto" />
              <h1 className="text-xl md:text-2xl font-bold text-primary">{title}</h1>
            </Link>
          )}
        </div>

        {/* 右側: ハンバーガーメニューまたは戻るボタン */}
        {user && showMenu ? (
          <div className="relative" ref={menuRef}>
            <Button
              ref={triggerRef}
              variant="ghost"
              size="sm"
              onClick={() => (menuOpen ? closeMenu() : openMenu())}
              aria-label="メニュー"
              aria-expanded={menuOpen}
            >
              {menuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </Button>

            {menuOpen && (
              <>
                {/* PC: 従来のドロップダウン（md 以上で表示） */}
                <div className="hidden md:block absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border z-50">
                  {renderMenuBody(user)}
                </div>

                {/* SP: 背景オーバーレイ＋右端ドロワー（md 未満で表示） */}
                <div className="md:hidden">
                  <div
                    className="fixed inset-0 bg-black/40 z-40"
                    onClick={closeMenu}
                    aria-hidden="true"
                  />
                  <div
                    ref={drawerRef}
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                    aria-label="メニュー"
                    className="fixed top-0 right-0 h-full w-72 max-w-[85%] bg-white shadow-xl z-50 overflow-y-auto animate-drawer-in focus:outline-none"
                  >
                    <div className="flex justify-end p-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={closeMenu}
                        aria-label="メニューを閉じる"
                      >
                        <X className="w-6 h-6 text-gray-600" />
                      </Button>
                    </div>
                    {renderMenuBody(user)}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : backTo ? (
          <Link to={backTo}>
            <Button variant="secondary">{backLabel || 'ダッシュボードに戻る'}</Button>
          </Link>
        ) : null}
      </div>
      </header>
      {/* fixed ヘッダー分の高さを確保するスペーサー（ヘッダー内側 div と同じ h-14/md:h-16）。 */}
      <div className="h-14 md:h-16" aria-hidden="true" />

      {/* ログアウト確認（PC ドロップダウン・SP ドロワーの双方から到達する） */}
      <ConfirmDialog
        open={showLogoutConfirm}
        title="ログアウトしますか？"
        message="再度ご利用いただくには、もう一度ログインが必要です。"
        confirmLabel="ログアウト"
        onConfirm={handleLogout}
        onClose={() => setShowLogoutConfirm(false)}
      />
    </>
  );
}
